import { useCallback, useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye } from '@fortawesome/pro-solid-svg-icons/faEye';
import { faEyeSlash } from '@fortawesome/pro-solid-svg-icons/faEyeSlash';
import { colorPalette } from 'shared/constants';
import styles from './quality-metric-card.module.scss';
import { Loader } from '../../core';
import { Line } from 'react-chartjs-2';
import {
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  Chart as ChartJS,
} from 'chart.js';
import { useSelector } from 'react-redux';
import { getLang } from 'shared/store/selectors/lang.selector';
import annotationPlugin from 'chartjs-plugin-annotation';
import { trendDataPointColor, OPERATORS, yAxisRange } from 'shared/utils/quality-metrics.utils';
import { QualityMetricNoDataIcon } from 'src/images';

function createBottomLabels(trends) {
  if (!trends) return [];

  const monthFormatter = Intl.DateTimeFormat('en-US', { month: 'short' });
  return trends.map(item => monthFormatter.format(new Date(item.year, item.month - 1)));
}

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  annotationPlugin,
);

/**
 * Creates a line graph for the quality metric trends over time.
 * @param {{ goal: number, metricId: string, invert: boolean, minimumRecords: number, totalRecords: number }} { goal, metricId, invert, minimumRecords, totalRecords }
 */
function GraphLegend({ series, goal, invert, lang, hiddenDatasets, onToggleDataset }) {
  const goalLabel = `${lang.GOAL} ${invert ? OPERATORS.LT : OPERATORS.GT} ${goal}%`;
  const aboveLabel = lang.PERFORMED_ABOVE.replace('{goal}', goal);
  const belowLabel = lang.PERFORMED_BELOW.replace('{goal}', goal);

  return (
    <div className={styles.graphLegend}>
      {series.map((entry, index) => (
        <button
          key={entry.label ?? index}
          type="button"
          title={entry.label}
          className={`${styles.legendItem} ${styles.legendToggle} ${hiddenDatasets.has(index) ? styles.legendItemHidden : ''}`}
          onClick={() => onToggleDataset(index)}
          aria-pressed={!hiddenDatasets.has(index)}>
          <span className={styles.legendSymbol}>
            <span className={styles.legendLineSolid} style={{ borderTopColor: entry.color }} />
          </span>
          <span className={styles.legendLabel}>
            <FontAwesomeIcon
              icon={hiddenDatasets.has(index) ? faEyeSlash : faEye}
              className={styles.legendEyeIcon}
            />
            {entry.label}
          </span>
        </button>
      ))}
      <span className={styles.legendItem}>
        <span className={styles.legendSymbol}>
          <span className={styles.legendLineDashed} />
        </span>
        <span className={styles.legendLabel}>{goalLabel}</span>
      </span>
      <span className={styles.legendItem}>
        <span className={styles.legendSymbol}>
          <span
            className={styles.legendDot}
            style={{ backgroundColor: colorPalette.greenPositive }}
          />
        </span>
        <span className={styles.legendLabel}>{invert ? belowLabel : aboveLabel}</span>
      </span>
      <span className={styles.legendItem}>
        <span className={styles.legendSymbol}>
          <span
            className={styles.legendDot}
            style={{ backgroundColor: colorPalette.redNegative }}
          />
        </span>
        <span className={styles.legendLabel}>{invert ? aboveLabel : belowLabel}</span>
      </span>
    </div>
  );
}

function MetricGraph({
  goal,
  invert = false,
  minimumRecords,
  totalRecords,
  trends = [],
  groupTrends = [],
  hideLabel = false,
  // The primary series is the viewer's own score on the clinician page, but the
  // group's on the management screen — hence the override.
  primaryLabel,
  // Replaces the default two-line shape wholesale, for callers comparing
  // several people on one chart. Each entry is
  // `{ label, trends, color, pointRadius?, goalColoredPoints? }`.
  series,
}) {
  const lang = useSelector(getLang('QUALITY_METRIC'));
  const chartRef = useRef(null);
  const [hiddenDatasets, setHiddenDatasets] = useState(new Set());

  const resolvedSeries = useMemo(() => {
    if (series?.length) return series;
    const base = [
      {
        label: primaryLabel ?? lang.MY_CASES,
        trends,
        color: colorPalette.primaryMain9,
        pointRadius: 5,
        goalColoredPoints: true,
      },
    ];
    if (groupTrends.length) {
      base.push({
        label: lang.DEP_CASES,
        trends: groupTrends,
        color: colorPalette.neutral5,
        pointRadius: 4,
        goalColoredPoints: true,
      });
    }
    return base;
  }, [series, trends, groupTrends, primaryLabel, lang]);

  const primaryTrends = resolvedSeries[0]?.trends ?? [];
  const labels = createBottomLabels(primaryTrends);

  // Only widen the axis across every line for an explicit multi-series caller —
  // the default shape keeps scaling to the primary line, as it always has.
  const rangeTrends = series?.length ? resolvedSeries.flatMap(entry => entry.trends ?? []) : trends;
  const { yAxisMin, yAxisMax } = yAxisRange({ trends: rangeTrends, goal });

  const handleToggleDataset = useCallback(datasetIndex => {
    setHiddenDatasets(prev => {
      const next = new Set(prev);
      if (next.has(datasetIndex)) {
        next.delete(datasetIndex);
      } else {
        next.add(datasetIndex);
      }
      return next;
    });

    const chart = chartRef.current;
    if (chart) {
      chart.setDatasetVisibility(datasetIndex, !chart.isDatasetVisible(datasetIndex));
      chart.update();
    }
  }, []);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'nearest',
      intersect: true,
    },
    scales: {
      x: {
        grid: {
          display: true,
          drawBorder: true,
          drawOnChartArea: true,
          color: colorPalette.neutral4,
          borderDash: [5, 5],
        },
        ticks: {
          display: true,
        },
      },
      y: {
        grid: {
          display: false,
          drawBorder: false,
        },
        min: yAxisMin,
        max: yAxisMax,
        ticks: {
          z: 2,
          callback: value => `${value}%`,
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        displayColors: true,
        usePointStyle: true,
        boxPadding: 4,
        bodyAlign: 'center',
        bodyFont: {
          weight: 'bold',
        },
        footerFont: {
          weight: 'normal',
        },
        footerAlign: 'center',
        callbacks: {
          title: function () {
            return null;
          },
          label: function (context) {
            return context.dataset.label;
          },
          footer: function (context) {
            const item = context[0];
            const trendData = resolvedSeries[item.datasetIndex]?.trends?.[item.dataIndex];

            if (!trendData?.hasData) {
              return lang.NO_DATA;
            }

            const year = String(trendData.year).slice(-2);
            const month = item.label;
            return `${month} '${year}: ${item.parsed.y}%`;
          },
        },
      },
      annotation: {
        annotations: {
          goalLine: {
            type: 'line',
            yMin: goal,
            yMax: goal,
            borderDash: [5, 5],
          },
        },
      },
    },
  };

  if (!primaryTrends.length) {
    return <Loader />;
  }

  if (totalRecords < minimumRecords) {
    return (
      <div className={styles.graphNoData}>
        <img
          src={QualityMetricNoDataIcon}
          alt={lang.NO_DATA_LOGGED_GRAPH}
          className={styles.graphNoDataIcon}
        />
        <p className={styles.graphNoDataText}>{lang.NO_DATA_LOGGED_GRAPH}</p>
      </div>
    );
  }

  return (
    <div className={styles.graphContainer}>
      {!hideLabel && <label className={styles.graphLabel}>{lang.TREND_GRAPH}</label>}
      <div className={styles.graphCanvasWrapper}>
        <Line
          ref={chartRef}
          options={options}
          data={{
            labels,
            datasets: resolvedSeries.map(entry => ({
              label: entry.label,
              data: (entry.trends ?? []).map(({ rate }) => rate),
              clip: false,
              tension: 0.5,
              borderColor: entry.color,
              pointBorderWidth: 1.5,
              pointRadius: entry.pointRadius ?? 4,
              pointBorderColor: colorPalette.neutral3,
              // Goal-coloured points read well for one or two lines; with
              // several they turn every series the same green/red, so a
              // comparison series keeps its own colour instead.
              pointBackgroundColor: entry.goalColoredPoints
                ? (entry.trends ?? []).map(({ rate, hasData }) =>
                    trendDataPointColor({ rate, hasData, goal, invert }),
                  )
                : entry.color,
            })),
          }}
        />
      </div>
      <GraphLegend
        series={resolvedSeries}
        goal={goal}
        invert={invert}
        lang={lang}
        hiddenDatasets={hiddenDatasets}
        onToggleDataset={handleToggleDataset}
      />
    </div>
  );
}

export { MetricGraph };
